/**
 * PATH: packages/email-hub/src/feature/components/EmailListStatePanel.tsx
 * FILE_ID: EMAIL-INBOX-COMPONENT-LISTSTATE-001
 * PURPOSE: Render the inbox/folder list-pane placeholder for every
 *          non-list state (loading, syncing, stuck, empty) + a pure
 *          state deriver so the page never shows a misleading panel.
 * WHY: ARC docs/architecture/EMAIL_HUB_LOADING_RESILIENCE_ARC.md — the
 *      inbox used to show a bare, indefinite "Loading…" spinner (or a
 *      misleading "No emails yet") while the backend returned an empty
 *      page DURING an active backfill sync. Operators read that as
 *      "the email loading is broken again". This panel distinguishes
 *      empty-because-syncing from empty-because-no-mail, and a watchdog
 *      flips a wedged spinner to a retryable "stuck" state so the
 *      spinner can never hang forever.
 * FLOW:
 *   ┌───────────────────────────┐   ┌──────────────────────┐   ┌────────────┐
 *   │ rowCount/loading/syncing/ │ → │ deriveEmailListState │ → │ <Panel/> / │
 *   │ watchdog/hasFilter        │   │ (pure I→O)           │   │  render    │
 *   └───────────────────────────┘   └──────────────────────┘   │  rows      │
 * DEPENDENCIES:
 *   - lucide-react: status icons
 *   - ../../_internal/lib/utils (cn): tenant-shared class joiner
 * RELATED:
 *   - feature/pages/InboxPage.tsx: primary consumer (also Starred/Sent)
 *   - feature/hooks/useEmails.ts: supplies loading/rows
 *   - useEmailSyncStatus: supplies isSyncing
 */
import { AlertCircle, Inbox, RefreshCw } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'

/** The mutually-exclusive render states for the list pane. `'list'`
 *  means "render the rows" (the caller maps emails itself). */
export type EmailListPaneState =
  | 'list'
  | 'loading'
  | 'syncing'
  | 'stuck'
  | 'empty'
  | 'empty-filtered'
  | 'sync-error'
  | 'identity-stale'

export interface DeriveEmailListStateInput {
  /** Rows actually on screen after client-side dedupe. */
  rowCount: number
  /** ``loadingInitial`` — the first page hasn't settled yet. */
  loading: boolean
  /** A foreground sync is actively running for this org/mailbox. */
  isSyncing: boolean
  /** The watchdog has tripped (initial load spinning too long, no rows). */
  watchdogTripped: boolean
  /** A category/search/other filter is applied (changes empty copy). */
  hasFilter: boolean
  /** EMAIL_HUB_COLD_START_RESILIENCE §D — the latest sync for this mailbox
   *  FAILED (expired token / connection error) and nothing is visible.
   *  Distinguishes "broken" from "still syncing" so we stop showing a
   *  hopeful spinner over a dead sync. */
  syncError?: boolean
  /** EMAIL_HUB_COLD_START_RESILIENCE §D — the BE reports mail exists for
   *  this org (a recent successful sync wrote rows) yet the list settled
   *  EMPTY for this tab. The classic cause is a stale ``pinTabIdentity``
   *  (tab pinned to a different user id than the live session), so the
   *  list query runs as the wrong user. A reload re-pins identity. */
  identityStale?: boolean
}

/**
 * Pure decision function: given the inbox's data + sync + watchdog
 * signals, pick exactly ONE list-pane state.
 *
 * Precedence (most → least specific):
 *   1. rows present                  → 'list'
 *   2. 0 rows + watchdog tripped     → 'stuck'   (never an infinite spinner —
 *                                       applies to BOTH the initial-load AND
 *                                       the settled-but-syncing dead-ends; see
 *                                       EMAIL_HUB_COLD_START_RESILIENCE_ARC §A)
 *   3. loading, fresh                → 'syncing' if a sync is active else 'loading'
 *   4. settled, 0 rows, syncing      → 'syncing' (NOT "no mail" — backfill window)
 *   5. settled, 0 rows, filtered     → 'empty-filtered'
 *   6. settled, 0 rows               → 'empty'
 *
 * The watchdog escalation is hoisted ABOVE the loading/syncing split because
 * the original bug was a full-pane spinner with zero rows that could hang for
 * the entire 300 s BE active-sync window: the `loading` flag clears in the
 * fetch `finally`, so a settled-empty inbox with `isSyncing` true previously
 * fell straight through to `'syncing'` forever with no escape. Now any
 * 0-row spinner the watchdog has been allowed to trip on becomes a retryable
 * `'stuck'` — the caller only arms the watchdog while a 0-row spinner is
 * actually on screen (loading OR an active sync), so this can never fire on a
 * populated inbox.
 */
export function deriveEmailListState(
  input: DeriveEmailListStateInput,
): EmailListPaneState {
  const {
    rowCount, loading, isSyncing, watchdogTripped, hasFilter,
    syncError, identityStale,
  } = input
  if (rowCount > 0) return 'list'
  // §D — KNOWN-broken states win over the hopeful spinner (but never over
  // actual rows). Identity-stale is the most specific (mail provably
  // exists, we just can't see it here), then a failed sync.
  if (identityStale) return 'identity-stale'
  // ...but a failed-sync row only wins ONCE THE INITIAL LOAD HAS SETTLED.
  // During the first page fetch (`loading`) a STALE failed-status row
  // from a previous sync would otherwise pre-empt the legitimate loading
  // spinner and FLASH "We couldn't sync this mailbox" for a beat before
  // the rows land (operator 2026-06-02: "it goes to can't sync this
  // mailbox and THEN it loads the emails"). Gate on `!loading` so the
  // spinner wins while we're still fetching; the watchdog still rescues a
  // wedged spinner via 'stuck', and a genuinely failed sync still settles
  // to 'sync-error' the moment `loading` clears with 0 rows.
  if (syncError && !isSyncing && !loading) return 'sync-error'
  if (watchdogTripped && (loading || isSyncing)) return 'stuck'
  if (loading) return isSyncing ? 'syncing' : 'loading'
  if (isSyncing) return 'syncing'
  return hasFilter ? 'empty-filtered' : 'empty'
}

/**
 * Decide whether the 'identity-stale' contradiction heuristic should fire.
 *
 * The heuristic — "a recent sync reported writing rows, yet this list
 * settled EMPTY with no filter → the list query is almost certainly running
 * as the wrong user (stale pinTabIdentity)" — is INBOX-ONLY. A synced
 * mailbox ALWAYS has inbox mail, so an empty inbox under those conditions is
 * a genuine contradiction. A FOLDER (Sent / Archived / Starred / Binned) is
 * legitimately empty even when the mailbox synced hundreds of messages
 * (e.g. zero starred mail), so firing the heuristic there turns every empty
 * folder into a scary "your session changed — reload" error instead of a
 * plain "no mail here". Gate on `isInboxFolder` so folders fall through to
 * the normal 'empty' state.
 *
 * BUG-2026-06-17-FOLDER-FRAME-FALSE-IDENTITY-STALE.
 */
export function shouldFlagIdentityStale(input: {
  /** Only the Inbox can produce a true wrong-user contradiction. */
  isInboxFolder: boolean
  /** First page / new-query still settling. */
  listLoading: boolean
  /** A foreground sync is running. */
  isSyncing: boolean
  /** Rows on screen after dedupe. */
  rowCount: number
  /** A category/search filter is applied (legitimately empties the list). */
  hasFilter: boolean
  /** The latest sync for this mailbox completed successfully. */
  latestSyncSucceeded: boolean
  /** How many messages that latest sync reported writing. */
  syncedCount: number
}): boolean {
  if (!input.isInboxFolder) return false
  return (
    !input.listLoading &&
    !input.isSyncing &&
    input.rowCount === 0 &&
    !input.hasFilter &&
    input.latestSyncSucceeded &&
    input.syncedCount > 0
  )
}

/** A single skeleton row that mirrors EmailRow's geometry (avatar + a
 *  sender line, subject line, snippet line, and a date stub). */
function EmailSkeletonRow() {
  return (
    <div className="px-3 py-2.5 flex items-start gap-2.5 border-b border-border/40">
      <div className="h-9 w-9 rounded-full bg-muted animate-pulse shrink-0" />
      <div className="flex-1 min-w-0 space-y-1.5 pt-0.5">
        <div className="flex items-center justify-between gap-2">
          <div className="h-3 w-28 rounded bg-muted animate-pulse" />
          <div className="h-2.5 w-10 rounded bg-muted/60 animate-pulse" />
        </div>
        <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-2.5 w-1/2 rounded bg-muted/60 animate-pulse" />
      </div>
    </div>
  )
}

/**
 * The inbox VISUAL FRAME — a list of skeleton rows painted instantly while
 * the first page is still in flight.
 *
 * Operator 2026-06-01: "it doesn't need to load everything at once, just
 * the visual frame first". The 'loading' / 'syncing' states used to render
 * a single centred spinner that withheld the ENTIRE list until the network
 * settled. This renders the row scaffold immediately so the inbox reads as
 * "here, filling in" rather than "blank, waiting". The real rows replace
 * the scaffold in place the moment the first page lands.
 */
export function EmailListSkeleton({ count = 9 }: { count?: number }) {
  return (
    <div data-testid="email-list-skeleton" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <EmailSkeletonRow key={i} />
      ))}
    </div>
  )
}

interface EmailListStatePanelProps {
  state: Exclude<EmailListPaneState, 'list'>
  /** Wired to the 'stuck' + 'sync-error' Retry button. */
  onRetry?: () => void
  /** EMAIL_HUB_COLD_START_RESILIENCE §D — wired to the 'identity-stale'
   *  Reload button. The host owns a SELF-LIMITING reload (the app's global
   *  reload breaker is inactive in some embeds, so the call site must not
   *  loop) — see InboxPage. */
  onReload?: () => void
  /** Optional human-readable detail for the 'sync-error' state (the
   *  truncated BE ``error_message``). */
  errorDetail?: string | null
  /** EMAIL_HUB_COLD_START_RESILIENCE §E — live "imported so far" count
   *  from the running sync row (active_sync.emails_synced). Rendered in the
   *  'syncing' state when > 0. */
  syncedSoFar?: number
  className?: string
}

/** Presentational placeholder for every non-list inbox state. */
export function EmailListStatePanel({
  state,
  onRetry,
  onReload,
  errorDetail,
  syncedSoFar,
  className,
}: EmailListStatePanelProps) {
  const wrap = (children: React.ReactNode) => (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-full text-center px-4 gap-2',
        className,
      )}
      data-testid="email-list-state-panel"
      data-state={state}
    >
      {children}
    </div>
  )

  if (state === 'loading') {
    return wrap(
      <>
        <RefreshCw className="h-5 w-5 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Loading…</span>
      </>,
    )
  }

  if (state === 'syncing') {
    return wrap(
      <>
        <RefreshCw className="h-5 w-5 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">
          Syncing your inbox…
        </span>
        <span className="text-xs text-muted-foreground/70">
          {/* EMAIL_HUB_COLD_START_RESILIENCE §E — the BE writes
              ``emails_synced`` incrementally on the running sync row, so
              this is a LIVE counter during a first import, not a static
              hint. Falls back to the generic copy before the first page
              commits. */}
          {syncedSoFar && syncedSoFar > 0
            ? `${syncedSoFar} imported so far…`
            : 'New mail will appear here as it arrives.'}
        </span>
      </>,
    )
  }

  if (state === 'stuck') {
    return wrap(
      <>
        <AlertCircle className="h-6 w-6 text-muted-foreground/40" />
        <span className="text-sm text-muted-foreground">
          This is taking longer than usual.
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        )}
      </>,
    )
  }

  if (state === 'sync-error') {
    return wrap(
      <>
        <AlertCircle className="h-6 w-6 text-amber-500/70" />
        <span className="text-sm text-muted-foreground">
          We couldn’t sync this mailbox.
        </span>
        {errorDetail && (
          <span
            className="text-xs text-muted-foreground/70 max-w-[280px] truncate"
            title={errorDetail}
          >
            {errorDetail}
          </span>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Retry sync
          </button>
        )}
      </>,
    )
  }

  if (state === 'identity-stale') {
    return wrap(
      <>
        <AlertCircle className="h-6 w-6 text-amber-500/70" />
        <span className="text-sm text-muted-foreground">
          This mailbox has messages we can’t show in this tab.
        </span>
        <span className="text-xs text-muted-foreground/70 max-w-[300px]">
          Your session changed since this tab opened. Reload to fix.
        </span>
        {onReload && (
          <button
            type="button"
            onClick={onReload}
            className="mt-1 px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Reload
          </button>
        )}
      </>,
    )
  }

  // 'empty' | 'empty-filtered'
  return wrap(
    <>
      <Inbox className="h-10 w-10 text-muted-foreground/20 mb-1" />
      <p className="text-sm text-muted-foreground">
        {state === 'empty-filtered'
          ? 'No emails match your filters'
          : 'No emails yet'}
      </p>
    </>,
  )
}
